import styled from 'styled-components'

import { UserShort } from '@root/entities/user-short'
import { UserShortAvatar } from '@root/entities/user-short/ui/user-short-avatar'
import { useTranslation } from '@root/shared/localization'
import { Separator, Skeleton } from '@root/shared/ui-kit'

import type { SearchUser } from '@root/entities/search-user'
import type { DelugeId } from '@root/entities/user-short'
import type { PropsWithChildren } from 'react'

interface UserConnectionsShortItemProps extends PropsWithChildren {
  followerDetail: SearchUser.SearchUser & {
    deployedTokensCount: number | undefined
  }
  withFollowings?: boolean

  onNavigateToUserProfile?: () => void
  onNavigateToFollowers?: () => void
  onNavigateToCoins?: () => void
  onNavigateToFollowings?: () => void
}

export const UserConnectionsShortItem = ({
  followerDetail,
  withFollowings,
  children,
  onNavigateToUserProfile,
  onNavigateToCoins,
  onNavigateToFollowers,
  onNavigateToFollowings,
}: UserConnectionsShortItemProps) => {
  const { t } = useTranslation()
  const name = UserShort.someName(followerDetail)

  return (
    <Root>
      <Top>
        <UserProfile>
          <ProfileLink type="button" onClick={onNavigateToUserProfile}>
            <UserShortAvatar withPreview size={24} userShort={followerDetail} />
          </ProfileLink>
          {name ? (
            <ProfileLink type="button" onClick={onNavigateToUserProfile}>
              <Name>{name}</Name>
            </ProfileLink>
          ) : undefined}
        </UserProfile>
        <Actions>{children}</Actions>
      </Top>
      <Bottom>
        <Count>
          <LabeledCount
            label={t('common.follower_other')}
            count={followerDetail.followersCount}
            countMinChars={3}
            delugeId={followerDetail.delugeId}
            onClick={onNavigateToFollowers}
          />
        </Count>

        {withFollowings ? (
          <>
            <Sep vertical />
            <Count>
              <LabeledCount
                label={t('common.following_other')}
                count={followerDetail.followingCount}
                countMinChars={3}
                delugeId={followerDetail.delugeId}
                onClick={onNavigateToFollowings}
              />
            </Count>
          </>
        ) : undefined}
        <Sep vertical />
        {followerDetail.deployedTokensCount &&
        followerDetail.deployedTokensCount > 0 ? (
          <Count>
            <LabeledCount
              label={t('common.created_coins')}
              count={followerDetail.deployedTokensCount}
              countMinChars={3}
              delugeId={followerDetail.delugeId}
              onClick={onNavigateToCoins}
            />
          </Count>
        ) : undefined}
      </Bottom>
    </Root>
  )
}

const Root = styled.div`
  background: var(--background-2);
  border-radius: 10px;
`

const ProfileLink = styled.button`
  all: unset;
  cursor: pointer;
`

const Top = styled.div`
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`
const Bottom = styled.div`
  border-top: 1px solid var(--background-3);
  display: flex;
  color: var(--text-9);
  align-items: center;
  flex-wrap: wrap;
`
const UserProfile = styled.div`
  color: var(--text-12);
  gap: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  word-break: break-word;
`
const Name = styled.div`
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  text-wrap: balance;
  color: var(--appearance-inv-1);
`
const Sep = styled(Separator)`
  height: 14px;
  background: var(--background-3);
`
const Actions = styled.div`
  display: flex;
  gap: 8px;
`
const Count = styled.div`
  flex: 1 0 auto;
  padding: 6px 10px 10px;
  text-align: center;
`

const LabeledCount = (props: {
  label: string
  count: number
  countMinChars: number
  delugeId: DelugeId
  onClick?: () => void
}) => {
  return (
    <LabeledCountRoot onClick={props.onClick}>
      <span>{props.label}</span>{' '}
      <LabeledCountCount $countMinChars={props.countMinChars}>
        ({props.count})
      </LabeledCountCount>
    </LabeledCountRoot>
  )
}
LabeledCount.Skeleton = (props: { width: number }) => (
  <LabeledCountRoot>
    <Skeleton.Line width={props.width} />
  </LabeledCountRoot>
)

const LabeledCountRoot = styled.button`
  all: unset;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  text-transform: capitalize;
  color: inherit;
`
const LabeledCountCount = styled.span<{ $countMinChars: number }>`
  display: inline-block;
  min-width: ${(x) => x.$countMinChars}ch;
`

const Skeleton_ = (props: {
  withFollowings?: boolean
  mode?: 'full' | 'top-only'
}) => {
  const mode = props.mode ?? 'full'
  return (
    <Root>
      <Top>
        <UserProfile>
          <div>
            <Skeleton variant={'circular'} width={24} height={24} />
          </div>
          <Name>
            <Skeleton.Line width={90} />
          </Name>
        </UserProfile>
        <Actions>
          <UnfollowSkeleton>
            <Skeleton.Surface />
          </UnfollowSkeleton>
          <KebabSkeleton>
            <Skeleton.Surface />
          </KebabSkeleton>
        </Actions>
      </Top>
      {mode === 'top-only' ? undefined : (
        <Bottom>
          <Count>
            <LabeledCount.Skeleton width={80} />
          </Count>
          {props.withFollowings ? (
            <>
              <Sep vertical />
              <LabeledCount.Skeleton width={90} />
            </>
          ) : undefined}
          <Sep vertical />
          <Count>
            <LabeledCount.Skeleton width={90} />
          </Count>
        </Bottom>
      )}
    </Root>
  )
}

UserConnectionsShortItem.Skeleton = (props: {
  withFollowings?: boolean
  mode?: 'full' | 'top-only'
}) => <Skeleton_ {...props} />
const UnfollowSkeleton = styled.div`
  width: 51px;
  height: 24px;
  position: relative;
`
const KebabSkeleton = styled.div`
  width: 24px;
  height: 24px;
  position: relative;
`
